export type Service = {
  title: string;
  description: string;
  points: string[];
  // optional structured metadata for server-side matching
  minBudget?: number;
  maxBudget?: number;
  typicalDeadlineDays?: number;
  qualifications?: string[];
};

export type Area = {
  category: string;
  items: string[];
};

export type Highlight = {
  label: string;
  value: string;
};

export type SiteContent = {
  siteTitle: string;
  tagline: string;
  hero: {
    headline: string;
    paragraph: string;
    features: string[];
  };
  services: Service[];
  expertise: {
    areas: Area[];
    certifications: string[];
    highlights: Highlight[];
  };
  contact: {
    email: string;
    phone: string;
    linkedin: string;
  };
  backgroundImages?: {
    hero?: string;
    services?: string;
    expertise?: string;
    professionals?: string;
  };
};

export const defaultContent: SiteContent = {
  siteTitle: 'Clinical Trial Expertise',
  tagline: 'Expert Clinical Trial Consulting Services',
  hero: {
    headline: 'Where Clinical Minds Connect',
    paragraph:
      'Connect clinical trial consultants with pharmaceutical companies and CROs to deliver high-quality clinical trial projects. Our platform enables seamless collaboration while ensuring strict compliance with local laws, SAHPRA regulations, and international clinical trial guidelines. Together, we accelerate innovation responsibly.',
    features: ['Expert Consultants & Companies Connected', 'Secure Project Delivery Platform'],
  },
  services: [
    {
      title: 'Trial Design & Protocol Development',
      description:
        'Comprehensive clinical trial design, protocol development, and regulatory strategy for Phase I-IV studies.',
      points: [
        'Study Design Optimization',
        'Protocol Writing',
        'Regulatory Strategy',
        'Patient Recruitment Strategy',
      ],
      minBudget: 20000,
      maxBudget: 250000,
      typicalDeadlineDays: 60,
      qualifications: ['GCP', 'PMP'],
    },
    {
      title: 'Regulatory & Compliance Consultation',
      description:
        'Expert guidance on SAHPRA and African regulatory regulations, CTAs, ethics committee submissions, and regulatory pathway optimization for clinical trials across Africa.',
      points: [
        'CTA/IND Preparation for SAHPRA & African Regulators',
        'Regulatory Interactions & Submissions',
        'Ethics Committee & REC Submissions',
        'Compliance Audits & Documentation',
      ],
      minBudget: 10000,
      maxBudget: 150000,
      typicalDeadlineDays: 30,
      qualifications: ['GCP', 'African Regulatory Experience'],
    },
    {
      title: 'SOP Writing & Proofreading',
      description:
        'Professional development and review of Standard Operating Procedures for clinical trial sites, ensuring compliance with GCP and regulatory requirements.',
      points: [
        'Custom SOP Development',
        'SOP Review & Proofreading',
        'Compliance Gap Analysis',
        'Template Customization',
      ],
      minBudget: 3000,
      maxBudget: 50000,
      typicalDeadlineDays: 21,
      qualifications: ['GCP', 'Site Management'],
    },
    {
      title: 'Quality Assurance',
      description:
        'Comprehensive quality assurance and audit services for clinical trials, including internal audits, site inspections, regulatory inspection readiness, and SAHPRA compliance audits. We can perform audits on behalf of sponsors at trial sites or internally.',
      points: [
        'Internal Sponsor Audits',
        'Site Quality Assurance Audits',
        'Pre-Audit Readiness Assessment',
        'Mock Audits & Gap Identification',
        'Document Preparation & Compliance Review',
        'Regulatory Inspection Preparation',
      ],
      minBudget: 5000,
      maxBudget: 75000,
      typicalDeadlineDays: 14,
      qualifications: ['GCP', 'Audit Experience', 'Quality Assurance'],
    },
    {
      title: 'Site Staff Training',
      description:
        'Tailored training programs for clinical trial site personnel, covering GCP, protocol-specific procedures, and regulatory compliance.',
      points: [
        'GCP Certification Training',
        'Protocol-Specific Training',
        'Regulatory Compliance Workshops',
        'Site Coordinator Development',
      ],
      minBudget: 2000,
      maxBudget: 40000,
      typicalDeadlineDays: 30,
      qualifications: ['GCP', 'Training Certified'],
    },
    {
      title: 'Medical Writing & Information Services',
      description:
        'Comprehensive medical writing for clinical and regulatory documents, publications, and medical information support for approved medications.',
      points: [
        'Clinical Study Reports (CSRs)',
        'Regulatory Submission Documents',
        'Scientific Publications & Manuscripts',
        'Medical Inquiry Response Development',
        'Product Information Materials',
        'Healthcare Professional Support',
      ],
      minBudget: 3000,
      maxBudget: 80000,
      typicalDeadlineDays: 30,
      qualifications: ['Medical Writing', 'Scientific Writing', 'Medical Degree'],
    },
    {
      title: 'Clinical Trial Monitoring',
      description:
        'Professional on-site and remote monitoring services ensuring protocol adherence, data quality, regulatory compliance, and participant safety throughout all phases of clinical trials.',
      points: [
        'On-Site & Remote Trial Monitoring',
        'Source Data Verification (SDV)',
        'Data Quality & Completeness Review',
        'Protocol Compliance Assessment',
        'Informed Consent Verification',
        'Adverse Event & Safety Monitoring',
        'Regulatory Document Review',
        'Site Performance Evaluation',
      ],
      minBudget: 12000,
      maxBudget: 180000,
      typicalDeadlineDays: 7,
      qualifications: ['GCP', 'Clinical Monitoring Certification', 'ICH-GCP Training'],
    },
    {
      title: 'Project Management',
      description:
        'End-to-end clinical trial project management services ensuring on-time, on-budget delivery while maintaining quality standards and regulatory compliance across all trial phases.',
      points: [
        'Project Planning & Timeline Development',
        'Budget Management & Resource Allocation',
        'Vendor & CRO Coordination',
        'Risk Assessment & Mitigation',
        'Stakeholder Communication & Reporting',
        'Milestone Tracking & Deliverables',
        'Quality Metrics & KPI Monitoring',
        'Trial Closeout & Documentation',
      ],
      minBudget: 15000,
      maxBudget: 200000,
      typicalDeadlineDays: 14,
      qualifications: ['PMP', 'Clinical Project Management', 'GCP'],
    },
    {
      title: 'Quality Oversight',
      description:
        'Integrated quality oversight combining quality assurance audits with project management to ensure trials meet quality standards while staying on schedule and within budget.',
      points: [
        'Quality Systems Development & Implementation',
        'Ongoing Quality Monitoring & Audits',
        'Project Progress & Performance Tracking',
        'Risk Assessment & Mitigation Planning',
        'Compliance & Quality Metrics Reporting',
        'Stakeholder Quality & Timeline Communication',
      ],
      minBudget: 20000,
      maxBudget: 250000,
      typicalDeadlineDays: 7,
      qualifications: ['GCP', 'PMP', 'Quality Assurance', 'Clinical Audit Experience'],
    },
  ],
  expertise: {
    areas: [
      {
        category: 'Compliance & Regulatory Adherence',
        items: [
          'SAHPRA Regulatory Compliance',
          'GCP (Good Clinical Practice) Standards',
          'Local Laws & International Guidelines',
          'Ethics Committee & Safety Compliance',
        ],
      },
      {
        category: 'Clinical Trial Phases',
        items: [
          'Phase I Safety Studies',
          'Phase II Efficacy',
          'Phase III Pivotal Trials',
          'Phase IV Post-Marketing',
        ],
      },
      {
        category: 'Therapeutic Areas',
        items: ['Oncology', 'Cardiovascular', 'Metabolic/Endocrinology', 'Immunology'],
      },
      {
        category: 'African Regulatory Bodies',
        items: [
          'SAHPRA (South African Health Products Regulatory Authority)',
          'African Drug Regulatory Authorities',
          'Independent Review Boards (IRBs)',
          'Research Ethics Committees (RECs)',
          'National Health Research Ethics Councils',
        ],
      },
    ],
    certifications: [
      'Certified Clinical Research Associate (CCRA)',
      'Good Clinical Practice (GCP) Certified',
      'Project Management Professional (PMP)',
    ],
    highlights: [
      { label: 'Trials Supported', value: '250+' },
      { label: 'Years', value: '15+' },
      { label: 'Budgets Managed', value: 'R36B+' },
    ],
  },
  contact: {
    email: 'clement.xana.za@gmail.com',
    phone: '+27 66 297 3079',
    linkedin: 'https://www.linkedin.com/in/clinical-trial-expertise-cte-6b352a3a0',
  },
  backgroundImages: {
    hero: '',
    services: '',
    expertise: '',
    professionals: '',
  },
};

const STORAGE_KEY = 'siteContent:v1';

export function loadContent(): SiteContent {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw) as SiteContent;
    }
  } catch (e) {
    // fallthrough to default
  }
  return defaultContent;
}

export function saveContent(content: SiteContent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(content));
    return true;
  } catch (e) {
    return false;
  }
}

export function clearSavedContent() {
  localStorage.removeItem(STORAGE_KEY);
}
